import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react'

export interface EditorActivity {
  dirty: boolean
  pending?: boolean
  description: string
  onDiscard?: () => void
  /** Saves everything this editor holds unsaved; true on an acknowledged save. */
  onSave?: () => Promise<boolean>
  /** Parent-owned drafts survive a section change within this exact editor route. */
  preserveWithinPath?: string
}
export const EditorActivityContext = createContext<{
  update: (id: string, activity: EditorActivity | null) => void
  activities: ReadonlyMap<string, EditorActivity>
} | null>(null)

/** Optional composition boundary. Existing standalone editors retain their guard. */
export function useEditorActivity(activity: EditorActivity) {
  const context = useContext(EditorActivityContext)
  const update = context?.update
  const id = useId()
  const { dirty, pending = false, description, onDiscard, onSave, preserveWithinPath } = activity
  const discardRef = useRef(onDiscard)
  useEffect(() => { discardRef.current = onDiscard }, [onDiscard])
  const discard = useCallback(() => discardRef.current?.(), [])
  const saveRef = useRef(onSave)
  useEffect(() => { saveRef.current = onSave }, [onSave])
  const canSave = !!onSave
  const save = useCallback(async () => (saveRef.current ? saveRef.current() : false), [])
  useEffect(() => {
    update?.(id, { dirty, pending, description, onDiscard: discard, onSave: canSave ? save : undefined, preserveWithinPath })
    return () => update?.(id, null)
  }, [update, id, dirty, pending, description, discard, canSave, save, preserveWithinPath])
  return context !== null
}

export function useEditorActivityState() {
  const context = useContext(EditorActivityContext)
  const activities = [...(context?.activities.values() ?? [])]
  return { dirty: activities.some(activity => activity.dirty), pending: activities.some(activity => activity.pending) }
}

/** Serialize a writer and expose its full acknowledgement cycle to the workspace. */
export function useEditorWrite(description: string, errorMessage: string) {
  const [pending, setPending] = useState(false)
  const busy = useRef(false)
  const context = useContext(EditorActivityContext)
  const id = useId()
  const write = async (operation: () => Promise<{ error?: string | null }>) => {
    if (busy.current) return { error: errorMessage }
    busy.current = true
    setPending(true)
    // The operation owns this registration, not the row's render lifetime.
    // Refetching a list can unmount a row before its acknowledgement arrives.
    const update = context?.update
    update?.(id, { dirty: false, pending: true, description })
    try { return await operation() }
    catch { return { error: errorMessage } }
    finally { busy.current = false; setPending(false); update?.(id, null) }
  }
  return { pending, write }
}
